import { useState } from "react";
import type { OcFlowState, OcPortalConfig } from "../App";
import { ocSendCode } from "../lib/oc-api";

interface OcStep2ChannelProps {
  config: OcPortalConfig;
  state: OcFlowState;
  setState: (next: OcFlowState) => void;
}

function ocReadSlug(): string {
  if (typeof window === "undefined") return "";
  return window.location.pathname.split("/").filter(Boolean)[0] ?? "";
}

function OcEmailIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-6 w-6"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

function OcPhoneIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-6 w-6"
      aria-hidden="true"
    >
      <rect x="7" y="2.5" width="10" height="19" rx="2.25" />
      <path d="M11 18.5h2" />
    </svg>
  );
}

function OcSpinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin text-slate-500"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z"
      />
    </svg>
  );
}

export function OcStep2Channel({ config, state, setState }: OcStep2ChannelProps) {
  const [pendingChannel, setPendingChannel] = useState<"email" | "sms" | null>(null);
  // Start clean — only show an error after the user picks a channel and the
  // send actually fails. Any errorMessage left on `state` by the prior step
  // is intentionally ignored: showing it before the user does anything is
  // confusing UX and made past failures look current.
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const channels = state.allowedChannels ?? [];
  const primary = config.branding?.primaryColor || "#0ea5e9";

  async function onSelect(channel: "email" | "sms") {
    if (pendingChannel) return;
    if (!state.sessionId) {
      setErrorMessage("Your session has expired. Please start over.");
      return;
    }
    setPendingChannel(channel);
    setErrorMessage(null);
    const slug = ocReadSlug();
    try {
      const res = await ocSendCode(slug, {
        sessionId: state.sessionId,
        channel,
      });

      if (!res.error) {
        setState({
          ...state,
          step: "verify",
          selectedChannel: channel,
          errorMessage: undefined,
        });
        return;
      }

      const err = res.error;
      if (
        err === "channel_unavailable" ||
        err === "email_not_configured" ||
        err === "smtp_connection_unavailable"
      ) {
        setErrorMessage("We could not send to that channel. Try a different option.");
        setPendingChannel(null);
        return;
      }

      if (err === "session_invalid") {
        setErrorMessage("Your session has expired. Please start over.");
        setPendingChannel(null);
        return;
      }

      if (err === "send_failed") {
        setErrorMessage(
          "Something went wrong sending your code. Please try again or contact support."
        );
        setPendingChannel(null);
        return;
      }

      if (err === "rate_limited") {
        setErrorMessage(
          "Too many attempts. Please wait a few minutes and try again."
        );
        setPendingChannel(null);
        return;
      }

      if (err === "turnstile_failed") {
        setErrorMessage("Verification failed. Please reload the page and try again.");
        setPendingChannel(null);
        return;
      }

      if (err === "internal_error") {
        setErrorMessage(
          "We hit a problem on our end. Please try again in a moment or contact your IT helpdesk."
        );
        setPendingChannel(null);
        return;
      }

      setErrorMessage(`Something went wrong (${err}). Please try again.`);
      setPendingChannel(null);
    } catch {
      setErrorMessage("We could not reach the server. Please check your connection and try again.");
      setPendingChannel(null);
    }
  }

  function onBack() {
    setState({
      ...state,
      step: "identifier",
      identifier: undefined,
      sessionId: undefined,
      allowedChannels: undefined,
      selectedChannel: undefined,
      errorMessage: undefined,
    });
  }

  return (
    <div className="oc-step oc-step-channel">
      <h1 className="text-2xl font-semibold text-slate-900 mb-2">
        Choose where to receive your code
      </h1>
      <p className="text-sm text-slate-600 mb-6">
        Pick a recovery channel on file. We will send a one-time verification code to confirm it is you.
      </p>

      <div className="space-y-3">
        {channels.length === 0 ? (
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-700">
            No recovery channels are available for this account. Please contact support.
          </div>
        ) : null}

        {channels.map((c) => {
          const isPending = pendingChannel === c.channel;
          const isDisabled = pendingChannel !== null;
          const label = c.channel === "email" ? "Email" : "Text message (SMS)";
          return (
            <button
              key={c.channel + ":" + c.target}
              type="button"
              onClick={() => onSelect(c.channel)}
              disabled={isDisabled}
              style={{ borderColor: isPending ? primary : undefined }}
              className="group flex w-full items-center gap-3 rounded-md border border-slate-200 bg-white px-4 py-3 text-left transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-600"
                style={{ color: primary }}
                aria-hidden="true"
              >
                {c.channel === "email" ? <OcEmailIcon /> : <OcPhoneIcon />}
              </span>
              <span className="flex-1">
                <span className="block text-sm font-medium text-slate-900">{label}</span>
                <span className="block text-sm text-slate-600">{c.target}</span>
              </span>
              {isPending ? <OcSpinner /> : null}
            </button>
          );
        })}
      </div>

      {errorMessage ? (
        <div
          role="alert"
          className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {errorMessage}
        </div>
      ) : null}

      <div className="mt-6">
        <button
          type="button"
          onClick={onBack}
          disabled={pendingChannel !== null}
          className="text-sm font-medium text-slate-600 underline-offset-4 hover:text-slate-900 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
        >
          &larr; Back
        </button>
      </div>
    </div>
  );
}

export default OcStep2Channel;
